import type { AbilityService } from '@efdigital/ll-addon-auth';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import {
  buildCreateConsumedToolDeployment,
  buildUpdateConsumedToolDeployment,
} from '@efdigital/ll-admin-v2/data/builders/lti/lti-consumed-tool-deployments';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import type { LtiConsumedToolDeployment } from '@efdigital/ll-admin-v2/data/types/lti';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

const DEFAULT_RESOURCE_LINK_CLAIM_URL =
  'https://purl.imsglobal.org/spec/lti/claim/resource_link';
const DEFAULT_RESOURCE_LINK_CLAIM_KEY = 'id';

export class EditLtiConsumedToolDeployment extends Component<
  EditRecordSignature<
    LtiConsumedToolDeployment,
    {
      platformId: string;
      consumedToolId: string;
      initialState: 'readonly' | 'edit';
    }
  >
> {
  @service declare store: Store;
  @service declare ability: AbilityService;

  setPlatformDeploymentId = (event: Event) => {
    this.args.item.platformDeploymentId = (
      event.target as HTMLInputElement
    ).value;
  };

  get useCustomResourceLinkClaimPath(): boolean {
    return this.args.item.resourceLinkClaimPath !== null;
  }

  get resourceLinkClaimUrl(): string {
    return (
      this.args.item.resourceLinkClaimPath?.[0] ??
      DEFAULT_RESOURCE_LINK_CLAIM_URL
    );
  }

  get resourceLinkClaimKey(): string {
    return (
      this.args.item.resourceLinkClaimPath?.[1] ??
      DEFAULT_RESOURCE_LINK_CLAIM_KEY
    );
  }

  toggleUseCustomResourceLinkClaimPath = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (target.checked) {
      this.args.item.resourceLinkClaimPath = [
        DEFAULT_RESOURCE_LINK_CLAIM_URL,
        DEFAULT_RESOURCE_LINK_CLAIM_KEY,
      ];
      return;
    }

    this.args.item.resourceLinkClaimPath = null;
  };

  setResourceLinkClaimUrl = (event: Event) => {
    const claimUrl = (event.target as HTMLInputElement).value;
    this.args.item.resourceLinkClaimPath = [
      claimUrl,
      this.resourceLinkClaimKey,
    ];
  };

  setResourceLinkClaimKey = (event: Event) => {
    const claimKey = (event.target as HTMLInputElement).value;
    this.args.item.resourceLinkClaimPath = [
      this.resourceLinkClaimUrl,
      claimKey,
    ];
  };

  toggleIsActive = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.isActive = target.checked;
  };

  saveDeployment = () => {
    const deployment = this.args.item;

    if (deployment.isNew) {
      return this.store.request(
        buildCreateConsumedToolDeployment(
          deployment,
          this.args.platformId,
          this.args.consumedToolId
        )
      );
    }

    return this.store.request(
      buildUpdateConsumedToolDeployment(
        deployment,
        this.args.platformId,
        this.args.consumedToolId
      )
    );
  };

  revertDeployment = () => {
    deepRollback(this.args.item, this.store);
  };

  <template>
    <EditRecord
      @editLabel="Edit deployment"
      @item={{@item}}
      @context={{@context}}
      @canEdit={{true}}
      @maxDepth={{0}}
      @buttonSize="btn-md"
      @save={{this.saveDeployment}}
      @revert={{this.revertDeployment}}
      @initialState={{@initialState}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Deployment details</legend>
          <div class="row">
            <div class="col-lg-6">

              <div class="mb-3">
                <label
                  class="form-label"
                  for="{{uniqueId}}-deployment-id"
                >Platform deployment ID</label>
                <input
                  id="{{uniqueId}}-deployment-id"
                  type="text"
                  class="form-control font-monospace"
                  required
                  value={{@item.platformDeploymentId}}
                  data-test-lti-deployment-id
                  {{on "input" this.setPlatformDeploymentId}}
                />
              </div>
            </div>
            <div class="col-lg-6">

              <div class="form-check form-switch mb-3">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-is-active"
                  checked={{@item.isActive}}
                  aria-checked={{@item.isActive}}
                  role="switch"
                  data-test-lti-deployment-is-active
                  {{on "change" this.toggleIsActive}}
                />
                <label class="form-check-label" for="{{uniqueId}}-is-active">
                  Accept LTI launches
                </label>
                <div class="form-text">
                  Turn on when you are ready for end users to use the tool.
                </div>
              </div>
            </div>
          </div>
        </fieldset>
        <fieldset disabled={{isReadOnly}} class="mb-0">
          <legend class="gud-eyebrow">Resource links</legend>
          <div class="form-check form-switch mb-3">
            <input
              type="checkbox"
              class="form-check-input"
              id="{{uniqueId}}-custom-resource-link-claim-path"
              checked={{this.useCustomResourceLinkClaimPath}}
              aria-checked={{this.useCustomResourceLinkClaimPath}}
              role="switch"
              data-test-lti-deployment-custom-resource-link-claim-path
              {{on "change" this.toggleUseCustomResourceLinkClaimPath}}
            />
            <label
              class="form-check-label"
              for="{{uniqueId}}-custom-resource-link-claim-path"
            >
              Custom resource link claim path
            </label>
            <div class="form-text">
              By default, the resource link ID is read from the standard LTI
              claim. Turn on to specify a different claim URL and property name.
            </div>
          </div>

          {{#if this.useCustomResourceLinkClaimPath}}
            <div class="mb-3">
              <label
                class="form-label"
                for="{{uniqueId}}-resource-link-claim-url"
              >Resource link claim URL</label>
              <input
                id="{{uniqueId}}-resource-link-claim-url"
                type="text"
                class="form-control font-monospace"
                value={{this.resourceLinkClaimUrl}}
                data-test-lti-deployment-resource-link-claim-url
                {{on "input" this.setResourceLinkClaimUrl}}
              />
              <div class="form-text">
                The LTI claim on the launch request that contains the resource
                link ID.
              </div>
            </div>

            <div class="mb-0">
              <label
                class="form-label"
                for="{{uniqueId}}-resource-link-claim-key"
              >Resource link claim key</label>
              <input
                id="{{uniqueId}}-resource-link-claim-key"
                type="text"
                class="form-control font-monospace"
                value={{this.resourceLinkClaimKey}}
                data-test-lti-deployment-resource-link-claim-key
                {{on "input" this.setResourceLinkClaimKey}}
              />
              <div class="form-text">
                The property name within that claim object.
              </div>
            </div>
          {{/if}}
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}

export default EditLtiConsumedToolDeployment;
